import {
  useId,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { map } from './core/MapView';
import { formatTime } from '../common/util/formatter';
import { mapIconKey } from './core/preloadImages';
import { normalizeIconScale, useAttributePreference } from '../common/util/preferences';
import { findFonts } from './core/mapUtil';
import { getOperationalStatusMeta } from '../common/util/deviceVisualStatus';

const EMPTY_COLLECTION = {
  type: 'FeatureCollection',
  features: [],
};

const MapPositions = ({
  positions = [],
  onMapClick,
  onMarkerClick,
  showStatus,
  selectedPosition,
  titleField,
}) => {
  const id = useId();
  const clusters = `${id}-clusters`;
  const selected = `${id}-selected`;

  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const preferredIconScale = useAttributePreference('iconScale', desktop ? 0.75 : 1);
  const iconScale = normalizeIconScale(preferredIconScale, desktop ? 0.75 : 1);

  const devices = useSelector((state) => state.devices.items);
  const selectedDeviceId = useSelector((state) => state.devices.selectedId);

  const mapCluster = useAttributePreference('mapCluster', true);
  const directionType = useAttributePreference('mapDirection', 'selected');

  const onMapClickRef = useRef(onMapClick);
  const onMarkerClickRef = useRef(onMarkerClick);

  const cacheRef = useRef({
    snapshotsBySource: {
      [id]: new Map(),
      [selected]: new Map(),
    },
    featureCollections: {
      [id]: EMPTY_COLLECTION,
      [selected]: EMPTY_COLLECTION,
    },
    serialized: {
      [id]: '',
      [selected]: '',
    },
  });

  useEffect(() => {
    onMapClickRef.current = onMapClick;
  }, [onMapClick]);

  useEffect(() => {
    onMarkerClickRef.current = onMarkerClick;
  }, [onMarkerClick]);

  const createFeaturePayload = useCallback((allDevices, position, selectedPositionId) => {
    const device = allDevices?.[position?.deviceId];

    if (!device) {
      return null;
    }

    const statusMeta = getOperationalStatusMeta(device, position);

    const showDirection = (() => {
      switch (directionType) {
        case 'none':
          return false;
        case 'all':
          return Number(position?.course) > 0;
        default:
          return selectedPositionId === position?.id && Number(position?.course) > 0;
      }
    })();

    return {
      id: position.id,
      deviceId: position.deviceId,
      name: device.name || '',
      fixTime: formatTime(position.fixTime, 'seconds'),
      category: mapIconKey(device.category),
      color: showStatus ? statusMeta.mapColor : 'neutral',
      rotation: Number(position.course) || 0,
      direction: showDirection,
      latitude: Number(position.latitude),
      longitude: Number(position.longitude),
    };
  }, [directionType, showStatus]);

  const baseLayout = useMemo(() => ({
    'icon-image': '{category}-{color}',
    'icon-size': iconScale,
    'icon-allow-overlap': true,
    'text-field': `{${titleField || 'name'}}`,
    'text-allow-overlap': true,
    'text-anchor': 'bottom',
    'text-offset': [0, -2 * iconScale],
    'text-font': findFonts(map),
    'text-size': 12,
    'symbol-sort-key': ['get', 'id'],
  }), [iconScale, titleField]);

  const directionLayout = useMemo(() => ({
    'icon-image': 'direction',
    'icon-size': iconScale,
    'icon-allow-overlap': true,
    'icon-rotate': ['get', 'rotation'],
    'icon-rotation-alignment': 'map',
  }), [iconScale]);

  useEffect(() => {
    if (!map) return undefined;

    const onMouseEnter = () => {
      const canvas = map.getCanvas?.();
      if (canvas) {
        canvas.style.cursor = 'pointer';
      }
    };

    const onMouseLeave = () => {
      const canvas = map.getCanvas?.();
      if (canvas) {
        canvas.style.cursor = '';
      }
    };

    const handleMapClick = (event) => {
      if (!event.defaultPrevented && onMapClickRef.current) {
        onMapClickRef.current(event.lngLat.lat, event.lngLat.lng);
      }
    };

    const handleMarkerClick = (event) => {
      event.preventDefault();

      const feature = event.features?.[0];
      if (!feature || !onMarkerClickRef.current) {
        return;
      }

      onMarkerClickRef.current(
        feature.properties?.id,
        feature.properties?.deviceId,
      );
    };

    const handleClusterClick = async (event) => {
      event.preventDefault();

      const features = map.queryRenderedFeatures(event.point, {
        layers: [clusters],
      });

      const clusterFeature = features?.[0];
      if (!clusterFeature) {
        return;
      }

      const clusterId = clusterFeature.properties?.cluster_id;
      const source = map.getSource(id);

      if (!source || clusterId == null) {
        return;
      }

      const zoom = await source.getClusterExpansionZoom(clusterId);

      map.easeTo({
        center: clusterFeature.geometry.coordinates,
        zoom,
      });
    };

    if (!map.getSource(id)) {
      map.addSource(id, {
        type: 'geojson',
        data: EMPTY_COLLECTION,
        cluster: mapCluster,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });
    }

    if (!map.getSource(selected)) {
      map.addSource(selected, {
        type: 'geojson',
        data: EMPTY_COLLECTION,
      });
    }

    [id, selected].forEach((sourceId) => {
      if (!map.getLayer(sourceId)) {
        map.addLayer({
          id: sourceId,
          type: 'symbol',
          source: sourceId,
          filter: ['!has', 'point_count'],
          layout: baseLayout,
          paint: {
            'text-halo-color': 'white',
            'text-halo-width': 2,
          },
        });
      }

      const directionLayerId = `direction-${sourceId}`;
      if (!map.getLayer(directionLayerId)) {
        map.addLayer({
          id: directionLayerId,
          type: 'symbol',
          source: sourceId,
          filter: [
            'all',
            ['!has', 'point_count'],
            ['==', 'direction', true],
          ],
          layout: directionLayout,
        });
      }

      map.on('mouseenter', sourceId, onMouseEnter);
      map.on('mouseleave', sourceId, onMouseLeave);
      map.on('click', sourceId, handleMarkerClick);
    });

    if (!map.getLayer(clusters)) {
      map.addLayer({
        id: clusters,
        type: 'symbol',
        source: id,
        filter: ['has', 'point_count'],
        layout: {
          'icon-image': 'background',
          'icon-size': iconScale,
          'text-field': '{point_count_abbreviated}',
          'text-font': findFonts(map),
          'text-size': 14,
        },
      });
    }

    map.on('mouseenter', clusters, onMouseEnter);
    map.on('mouseleave', clusters, onMouseLeave);
    map.on('click', clusters, handleClusterClick);
    map.on('click', handleMapClick);

    return () => {
      map.off('mouseenter', clusters, onMouseEnter);
      map.off('mouseleave', clusters, onMouseLeave);
      map.off('click', clusters, handleClusterClick);
      map.off('click', handleMapClick);

      [id, selected].forEach((sourceId) => {
        map.off('mouseenter', sourceId, onMouseEnter);
        map.off('mouseleave', sourceId, onMouseLeave);
        map.off('click', sourceId, handleMarkerClick);

        const directionLayerId = `direction-${sourceId}`;

        if (map.getLayer(directionLayerId)) {
          map.removeLayer(directionLayerId);
        }

        if (map.getLayer(sourceId)) {
          map.removeLayer(sourceId);
        }

        if (map.getSource(sourceId)) {
          map.removeSource(sourceId);
        }
      });

      if (map.getLayer(clusters)) {
        map.removeLayer(clusters);
      }
    };
  }, [id, selected, clusters, mapCluster, iconScale, baseLayout, directionLayout]);

  useEffect(() => {
    if (!map) return;

    const nextFeaturesBySource = {
      [id]: [],
      [selected]: [],
    };

    const nextSnapshotsBySource = {
      [id]: new Map(),
      [selected]: new Map(),
    };

    const previousSnapshotsBySource = cacheRef.current.snapshotsBySource;

    positions
      .filter((position) => Object.prototype.hasOwnProperty.call(devices || {}, position?.deviceId))
      .forEach((position) => {
        const payload = createFeaturePayload(
          devices,
          position,
          selectedPosition?.id,
        );

        if (
          !payload
          || !Number.isFinite(payload.latitude)
          || !Number.isFinite(payload.longitude)
        ) {
          return;
        }

        const sourceId = payload.deviceId === selectedDeviceId ? selected : id;

        const snapshot = {
          id: payload.id ?? null,
          deviceId: payload.deviceId ?? null,
          name: payload.name || '',
          category: payload.category || 'default',
          color: payload.color || 'neutral',
          rotation: payload.rotation || 0,
          direction: Boolean(payload.direction),
          latitude: payload.latitude,
          longitude: payload.longitude,
          fixTime: payload.fixTime || '',
        };

        nextSnapshotsBySource[sourceId].set(snapshot.deviceId, snapshot);

        const previousSnapshot = previousSnapshotsBySource[sourceId]?.get(snapshot.deviceId);

        const unchanged = previousSnapshot
          && previousSnapshot.id === snapshot.id
          && previousSnapshot.deviceId === snapshot.deviceId
          && previousSnapshot.name === snapshot.name
          && previousSnapshot.category === snapshot.category
          && previousSnapshot.color === snapshot.color
          && previousSnapshot.rotation === snapshot.rotation
          && previousSnapshot.direction === snapshot.direction
          && previousSnapshot.latitude === snapshot.latitude
          && previousSnapshot.longitude === snapshot.longitude
          && previousSnapshot.fixTime === snapshot.fixTime;

        if (unchanged && previousSnapshot.__feature) {
          nextFeaturesBySource[sourceId].push(previousSnapshot.__feature);
          return;
        }

        const feature = {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [snapshot.longitude, snapshot.latitude],
          },
          properties: {
            id: snapshot.id,
            deviceId: snapshot.deviceId,
            name: snapshot.name,
            fixTime: snapshot.fixTime,
            category: snapshot.category,
            color: snapshot.color,
            rotation: snapshot.rotation,
            direction: snapshot.direction,
          },
        };

        snapshot.__feature = feature;
        nextSnapshotsBySource[sourceId].set(snapshot.deviceId, snapshot);
        nextFeaturesBySource[sourceId].push(feature);
      });

    [id, selected].forEach((sourceId) => {
      const collection = {
        type: 'FeatureCollection',
        features: nextFeaturesBySource[sourceId],
      };

      const serialized = JSON.stringify(collection);

      if (serialized !== cacheRef.current.serialized[sourceId]) {
        map.getSource(sourceId)?.setData(collection);
        cacheRef.current.featureCollections[sourceId] = collection;
        cacheRef.current.serialized[sourceId] = serialized;
      }

      cacheRef.current.snapshotsBySource[sourceId] = nextSnapshotsBySource[sourceId];
    });
  }, [
    id,
    selected,
    devices,
    positions,
    selectedDeviceId,
    selectedPosition,
    createFeaturePayload,
  ]);

  return null;
};

export default MapPositions;
